import type { Engine } from '../core/Engine';
import type { GameModule } from '../core/GameModule';
import type { Renderable } from '../renderer/RenderPipeline';
import type { Camera } from '../renderer/Camera';
import { InstancedShipRenderer } from './InstancedShipRenderer';
import { SHIP_TYPE_DEFS, SHIP_TYPE_COUNT } from '../config/ShipTypes';
import { FleetFormation, type TypeSlice } from '../systems/FleetFormation';
import type { FleetState } from '../systems/FleetState';
import type { DebugCircleRenderer } from './DebugCircleRenderer';
import { loadGlb } from '../renderer/GlbParser';

/**
 * Orchestrates one InstancedShipRenderer per ship type.
 * Computes formation each frame, feeds instance data, and renders all.
 */
export class FleetRenderer implements Renderable, GameModule {
  readonly name = 'FleetRenderer';

  private _renderers: InstancedShipRenderer[] = [];
  private _formation: FleetFormation;
  private _fleet: FleetState;
  private _debugCircle: DebugCircleRenderer | null = null;
  private _debugCirclesOn = false;
  private _visible = false;
  private _lineup = false;
  private _time = 0;

  constructor(gl: WebGL2RenderingContext, fleet: FleetState, spacing: number, maxRadius: number) {
    this._fleet = fleet;
    this._formation = new FleetFormation(spacing, maxRadius);
    for (let i = 0; i < SHIP_TYPE_COUNT; i++) {
      this._renderers.push(new InstancedShipRenderer(gl, SHIP_TYPE_DEFS[i]));
    }
  }

  get formation(): FleetFormation { return this._formation; }

  async loadModels(): Promise<void> {
    const loaded = new Map<string, ReturnType<typeof loadGlb>>();
    for (let i = 0; i < SHIP_TYPE_COUNT; i++) {
      const url = SHIP_TYPE_DEFS[i].modelUrl;
      if (!loaded.has(url)) loaded.set(url, loadGlb(url));
    }
    const meshes = new Map<string, Awaited<ReturnType<typeof loadGlb>>>();
    for (const [url, promise] of loaded) {
      meshes.set(url, await promise);
    }
    for (let i = 0; i < SHIP_TYPE_COUNT; i++) {
      const mesh = meshes.get(SHIP_TYPE_DEFS[i].modelUrl);
      if (mesh) await this._renderers[i].loadMesh(mesh);
    }
  }

  init(_engine: Engine): void {}
  update(dt: number): void { this._time += dt; }

  setVisible(v: boolean): void { this._visible = v; }
  set lineup(v: boolean) { this._lineup = v; }
  setDebugCircle(c: DebugCircleRenderer): void { this._debugCircle = c; }
  set debugCircles(on: boolean) {
    this._debugCirclesOn = on;
    if (this._debugCircle) this._debugCircle.setActive(on);
  }

  updateFormation(): void {
    if (!this._visible || !this._fleet.active) return;
    const comp = this._fleet.composition;
    this._formation.compute(
      comp,
      this._fleet.centerX, this._fleet.centerY, this._fleet.centerZ,
      this._fleet.dirX, this._fleet.dirZ, this._fleet.turnRate,
      this._time, this._lineup,
    );
    const slices = this._formation.slices;
    for (let i = 0; i < SHIP_TYPE_COUNT; i++) {
      const s: TypeSlice = slices[i];
      this._renderers[i].updateInstances(s.data, s.count);
    }

    if (this._debugCirclesOn && this._debugCircle) {
      const f = this._formation;
      this._debugCircle.setFromFormation(f.total, f.px, f.py, f.pz, f.fp);
    }
  }

  render(gl: WebGL2RenderingContext, camera: Camera): void {
    if (!this._visible) return;
    for (let i = 0; i < this._renderers.length; i++) {
      this._renderers[i].render(gl, camera, this._time);
    }
  }

  dispose(): void {
    for (let i = 0; i < this._renderers.length; i++) {
      this._renderers[i].dispose();
    }
  }
}
